import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { User, Play, LogOut, Map, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/config';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <Navbar />
        <div data-testid="dashboard-loading" className="flex items-center justify-center min-h-screen">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div data-testid="dashboard-header" className="mb-12">
            <h1 className="text-5xl font-bold text-amber-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Mi Dashboard
            </h1>
            <p className="text-xl text-gray-700">
              Bienvenido, {user.name}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Profile Card */}
            <div data-testid="profile-card" className="glass rounded-2xl p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center mr-4">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-amber-900">Perfil</h3>
                  <p className="text-sm text-gray-600">Tu información</p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Email:</span> {user.email}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Rol:</span> {user.role === 'admin' ? 'Administrador' : 'Usuario'}
                </p>
              </div>
            </div>

            {/* Map 3D Card */}
            <div data-testid="map3d-card" className="glass rounded-2xl p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center mr-4">
                  <Map className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-amber-900">Mapa 3D</h3>
                  <p className="text-sm text-gray-600">Exploración Interactiva</p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-700 mb-2">
                  Explora las Líneas de Nasca y Palpa con modelos 3D y puntos de interés histórico.
                </p>
                <Button
                  onClick={() => navigate('/map3d')}
                  className="btn-peru mt-2 w-full"
                  size="sm"
                >
                  Abrir Mapa
                </Button>
              </div>
            </div>

            {/* Blog Card */}
            <div data-testid="blog-card" className="glass rounded-2xl p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center mr-4">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-amber-900">Comunidad</h3>
                  <p className="text-sm text-gray-600">Blog nasca360.com</p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-700 mb-2">
                  Comparte publicaciones, experiencias y opiniones sobre la herencia de Nasca.
                </p>
                <Button
                  onClick={() => navigate('/blog')}
                  variant="outline"
                  className="w-full border-2 border-amber-700"
                  size="sm"
                >
                  Ir al Blog
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div data-testid="quick-actions" className="glass rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-amber-900 mb-6">Acciones Rápidas</h2>
            <div className="grid md:grid-cols-4 gap-4">
              <Button
                onClick={() => navigate('/gallery')}
                className="btn-peru py-6 flex flex-col items-center"
              >
                <Play className="w-6 h-6 mb-2" />
                Ver Galería
              </Button>
              <Button
                onClick={() => navigate('/map3d')}
                className="bg-white text-amber-900 border-2 border-amber-700 hover:bg-amber-50 py-6 flex flex-col items-center"
              >
                <Map className="w-6 h-6 mb-2" />
                Mapa 3D
              </Button>
              <Button
                onClick={() => navigate('/blog')}
                className="bg-white text-amber-900 border-2 border-amber-700 hover:bg-amber-50 py-6 flex flex-col items-center"
              >
                <BookOpen className="w-4 h-4 mb-2" />
                Blog de Comunidad
              </Button>
              <Button
                onClick={logout}
                variant="outline"
                className="border-2 border-red-300 text-red-600 hover:bg-red-50 py-6 flex flex-col items-center"
              >
                <LogOut className="w-6 h-6 mb-2" />
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;